/**
 * Events → Speakers ("השפעת דובר") — A2 end-to-end live module view.
 *
 * Composes the live `/v1/contract/speakers` envelope with the honesty
 * primitives (`<Freshness/>`, `<Denominator/>`, `<AnomalyChip/>`), the
 * per-MK `<Scorecard/>` leaderboard, and the `<SpeakerMatrix/>` chart.
 *
 * Speaker classification uses the STRUCTURED MK link fields on the
 * events table (three multipleRecordLinks → MK directory with party
 * affiliation). The free-text title/subtitle classifier is now a
 * fallback for legacy events without links. The honesty footer makes
 * that explicit so users know to populate the link fields when a known
 * headliner is missing.
 */
import type { Suite, ModuleSpec } from '../../../data/types/platform';
import { useEventsSpeakers } from '../../../data/queries/useEventsSpeakers';
import { SpeakerMatrix } from '../../../components/charts/SpeakerMatrix';
import { Scorecard, type ScorecardRow } from '../../../components/charts/Scorecard';
import type {
  SpeakerByFormat,
  SpeakerCell,
  TopSpeaker,
} from '../../../data/api/events';
import { AnomalyChip, Denominator, Freshness } from '../../../components/honesty';
import { Icon } from '../../../components/ui/Icon';
import { formatNumber } from '../../../utils/format';

export interface SpeakersViewProps {
  system?: Suite;
  module?: ModuleSpec;
}

function toEpoch(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Date.now();
}

/**
 * Order matters: principal first (the headline class), then senior,
 * guest, local-only. Mirrors the API's `SPEAKER_CLASSES` ordering.
 */
const SPEAKER_CLASS_LABELS: Record<string, { he: string; short: string }> = {
  principal: { he: 'דובר ראשי (בנט)', short: 'ראשי' },
  senior: { he: 'בכיר אחר (בנט 2026)', short: 'בכיר' },
  guest: { he: 'דובר אורח (יש עתיד / אחר)', short: 'אורח' },
  'local-only': { he: 'ללא דובר מזוהה', short: 'מקומי' },
};
const CLASS_ORDER = ['principal', 'senior', 'guest', 'local-only'] as const;

const FORMAT_LABELS: Record<string, string> = {
  rally: 'סיור',
  parlor: 'חוג בית',
  'town-hall': 'כנס',
  virtual: 'אירוע ווירטואלי',
};
const FORMAT_ORDER = ['rally', 'parlor', 'town-hall', 'virtual'] as const;

const PARTY_LABEL: Record<string, string> = {
  'בנט 2026': 'בנט 2026',
  'יש עתיד': 'יש עתיד',
};

export function SpeakersView({ system, module }: SpeakersViewProps) {
  const { data, isLoading, error } = useEventsSpeakers();

  return (
    <article className="module-view speakers-view module-page">
      <header className="mp-head">
        <div className="mp-eyebrow">
          <span>{system?.en ?? 'EVENTS'}</span>
          <span className="mp-sep">/</span>
          <span>{module?.he ?? 'השפעת דובר'}</span>
        </div>
        <h1>{module?.he ?? 'השפעת דובר'}</h1>
        <p className="mp-sub">
          {module?.sub ??
            'דוברים מובילים, סיווג ראשי / בכיר / אורח / מקומי, ובקרת תבנית — מבוסס על קישורי חברי כנסת מובְנים בכל אירוע.'}
        </p>
        <div className="mp-meta">
          {data && (
            <>
              <Freshness updatedAt={toEpoch(data.source_pulled_at)} />
              <span className="mp-dot" />
              <span>scope: {data.scope}</span>
              {data.meta.mock && (
                <>
                  <span className="mp-dot" />
                  <span>mock fixtures</span>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <div className="mp-body">
        {isLoading && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="loader" size={20} />
                <span>טוען נתוני השפעת דובר…</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="alert-triangle" size={20} />
                <span>שגיאה בטעינת המודול: {error.message}</span>
              </div>
            </div>
          </div>
        )}

        {data && <SpeakersBody envelope={data} />}
      </div>
    </article>
  );
}

interface SpeakersBodyProps {
  envelope: NonNullable<ReturnType<typeof useEventsSpeakers>['data']>;
}

function SpeakersBody({ envelope }: SpeakersBodyProps) {
  const matrix = envelope.data.matrix;
  const topSpeakers = envelope.data.top_speakers ?? [];

  // Per-class totals (events + n + weighted yes_show). Computed up
  // front so the KPI strip + top-cells panel can both read from it.
  const classTotals = CLASS_ORDER.map((cls) => {
    const row = matrix[cls] ?? {};
    let events = 0;
    let n = 0;
    let wnum = 0;
    let wden = 0;
    for (const fmt of FORMAT_ORDER) {
      const c = row[fmt];
      if (!c) continue;
      events += c.events;
      n += c.n;
      if (c.yes_show != null && c.events > 0) {
        wnum += c.yes_show * c.events;
        wden += c.events;
      }
    }
    return {
      id: cls,
      label: SPEAKER_CLASS_LABELS[cls]!.he,
      short: SPEAKER_CLASS_LABELS[cls]!.short,
      events,
      n,
      yes_show: wden > 0 ? wnum / wden : null,
    };
  });

  const totalClassified = classTotals.reduce((sum, c) => sum + c.events, 0);

  // Named-MK KPIs — derived from the structured leaderboard.
  const uniqueSpeakers = topSpeakers.length;
  const bennettCount = topSpeakers.filter((s) => s.party === 'בנט 2026').length;
  const yeshAtidCount = topSpeakers.filter((s) => s.party === 'יש עתיד').length;
  const localOnlyEvents =
    classTotals.find((c) => c.id === 'local-only')?.events ?? 0;

  // Top reportable cells (yes_show != null, events ≥ 3) — sorted desc.
  const reportableCells = collectReportableCells(matrix);
  const topCells = reportableCells.slice(0, 3);

  // Format the matrix for the chart primitive.
  const classes = CLASS_ORDER.map((id) => ({
    id,
    label: SPEAKER_CLASS_LABELS[id]!.he,
  }));
  const formats = FORMAT_ORDER.map((id) => ({
    id,
    label: FORMAT_LABELS[id]!,
  }));

  // Build Scorecard rows from the top_speakers list (already sorted desc
  // by events on the API side).
  const speakerRows: ScorecardRow[] = topSpeakers.map((s) => ({
    id: s.mk_id,
    label: s.name,
    sub: speakerSub(s),
    primary: s.attended,
    secondary: s.events,
    ...(s.yes_show != null ? { rate: s.yes_show } : {}),
  }));

  return (
    <>
      {/* Named-MK KPI strip — what the user actually wants to see. */}
      <div className="kpi-grid cols-4">
        <KpiCell
          label="דוברים בעלי שם"
          value={formatNumber(uniqueSpeakers)}
          sub={
            uniqueSpeakers === 0
              ? 'אין שדה דובר מאוכלס באירועים'
              : `${formatNumber(uniqueSpeakers)} ח״כ/ית בקטלוג`
          }
          focus={uniqueSpeakers > 0}
          accent={uniqueSpeakers > 0}
        />
        <KpiCell
          label="בנט 2026"
          value={formatNumber(bennettCount)}
          sub={`${bennettCount} מהדוברים מהמפלגה`}
        />
        <KpiCell
          label="יש עתיד"
          value={formatNumber(yeshAtidCount)}
          sub={`${yeshAtidCount} מהדוברים מהמפלגה`}
        />
        <KpiCell
          label="ללא דובר מזוהה"
          value={formatNumber(localOnlyEvents)}
          sub={`${localOnlyEvents} אירועים — מקומי בלבד`}
        />
      </div>

      {/* PROMINENT: Named-MK leaderboard. The whole point of the rewrite. */}
      <div className="panel">
        <div className="panel-head">
          <div className="title">דוברים מובילים</div>
          <div className="sub">
            דירוג חברי כנסת / רשימה לפי אירועים שהשתתפו בהם — מבוסס על
            שדות הקישור המובְנים בטבלת האירועים.
          </div>
        </div>
        <div style={{ padding: '8px 0' }}>
          {topSpeakers.length === 0 ? (
            <div className="mp-placeholder" style={{ padding: '16px' }}>
              <Icon name="info" size={16} />
              <span>
                אין אירוע עם שדה דובר (בהשתתפות / חברי יש עתיד / חברי בנט 2026)
                מאוכלס. ברגע שהשדה יאוכלס באיירטייבל, הדוברים יופיעו כאן.
              </span>
            </div>
          ) : (
            <Scorecard
              rows={speakerRows}
              secondaryLabel="אירועים"
              rateLabel="המרה"
              columns={{
                rank: '#',
                label: 'דובר',
                bar: 'הגעה',
                primary: 'הגעה',
                rate: 'המרה',
                secondary: 'אירועים',
              }}
            />
          )}
        </div>
      </div>

      {/* KPI strip — one per speaker class. Useful cross-cut. */}
      <div className="kpi-grid cols-4">
        {classTotals.map((c) => (
          <KpiCell
            key={c.id}
            label={c.label}
            value={formatNumber(c.events)}
            sub={
              c.events === 0
                ? 'אין אירועים בקטגוריה זו'
                : c.yes_show != null
                  ? `${Math.round(c.yes_show * 100)}% אישור→הגעה · n=${formatNumber(c.n)}`
                  : `n=${formatNumber(c.n)} · אין מספיק אירועים להמרה מהימנה`
            }
            focus={c.id === 'principal' && c.events > 0}
            accent={c.id === 'principal' && c.events > 0}
          />
        ))}
      </div>

      {/* Main matrix panel. */}
      <div className="panel">
        <div className="panel-head">
          <div className="title">המרת אישור→הגעה · דובר × תבנית</div>
          <div className="sub">
            בקרה מלאה. כל תא — n מצוטט. תאים עם פחות מ-3 אירועים מציגים &quot;—&quot;.
          </div>
        </div>
        <div style={{ padding: '18px' }}>
          {totalClassified === 0 ? (
            <div className="mp-placeholder">
              <Icon name="info" size={16} />
              <span>אין אירועים מסווגים בתקופה זו.</span>
            </div>
          ) : (
            <SpeakerMatrix
              classes={classes}
              formats={formats}
              cells={matrix as Record<string, Record<string, SpeakerCell>>}
            />
          )}
        </div>
      </div>

      {/* Top-3 reportable cells panel. */}
      {topCells.length > 0 && (
        <div className="panel">
          <div className="panel-head">
            <div className="title">תאים מובילים · אישור→הגעה</div>
            <div className="sub">המרת RSVP→הגעה הגבוהה ביותר (מתוך תאים עם 3+ אירועים)</div>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>סוג דובר</th>
                <th>תבנית</th>
                <th style={{ textAlign: 'right' }}>המרה</th>
                <th style={{ textAlign: 'right' }}>אירועים</th>
                <th style={{ textAlign: 'right' }}>n</th>
              </tr>
            </thead>
            <tbody>
              {topCells.map((t, i) => (
                <tr key={`${t.classId}:${t.formatId}:${i}`}>
                  <td>
                    <b>{SPEAKER_CLASS_LABELS[t.classId]?.he ?? t.classId}</b>
                  </td>
                  <td>{FORMAT_LABELS[t.formatId] ?? t.formatId}</td>
                  <td className="num-cell" style={{ textAlign: 'right' }}>
                    {Math.round((t.yes_show ?? 0) * 100)}%
                  </td>
                  <td className="num-cell" style={{ textAlign: 'right' }}>
                    {formatNumber(t.events)}
                  </td>
                  <td className="num-cell" style={{ textAlign: 'right' }}>
                    {formatNumber(t.n)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Honesty footer. */}
      <footer
        className="mv-foot"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 0 4px',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--ink-3)',
          alignItems: 'center',
        }}
      >
        <Denominator
          n={totalClassified}
          total={totalClassified}
          label="אירועים מסווגים"
        />
        <Denominator
          n={reportableCells.length}
          total={CLASS_ORDER.length * FORMAT_ORDER.length}
          label="תאים מהימנים (3+ אירועים)"
        />
        <span
          style={{
            padding: '4px 8px',
            border: '1px dashed var(--hairline-2, var(--hairline))',
            borderRadius: 4,
            color: 'var(--ink-3)',
            maxWidth: 540,
            lineHeight: 1.45,
          }}
        >
          סיווג דובר מבוסס על שלושת שדות הקישור לחברי כנסת באיירטייבל
          (בהשתתפות / חברי יש עתיד / חברי בנט 2026). אירועים ללא שדה
          מאוכלס נופלים לסיווג היוריסטי מטקסט הכותרת.
        </span>
        {envelope.meta.anomalies.map((a) => (
          <AnomalyChip
            key={a.key}
            severity={a.severity === 'critical' ? 'alert' : a.severity}
            reason={a.message}
          />
        ))}
      </footer>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

interface ReportableCell {
  classId: string;
  formatId: string;
  yes_show: number;
  events: number;
  n: number;
}

function collectReportableCells(matrix: SpeakerByFormat): ReportableCell[] {
  const out: ReportableCell[] = [];
  for (const cls of CLASS_ORDER) {
    const row = matrix[cls] ?? {};
    for (const fmt of FORMAT_ORDER) {
      const c = row[fmt];
      if (!c || c.events < 3 || c.yes_show == null) continue;
      out.push({
        classId: cls,
        formatId: fmt,
        yes_show: c.yes_show,
        events: c.events,
        n: c.n,
      });
    }
  }
  out.sort((a, b) => b.yes_show - a.yes_show);
  return out;
}

/**
 * Compose the Scorecard sub-line for a top_speaker row: party chip +
 * dominant format. Falls back gracefully when either is missing.
 */
function speakerSub(s: TopSpeaker): string {
  const parts: string[] = [];
  if (s.party) parts.push(PARTY_LABEL[s.party] ?? s.party);
  else parts.push('ללא שיוך');
  if (s.format_mix) parts.push(`רוב ${FORMAT_LABELS[s.format_mix] ?? s.format_mix}`);
  return parts.join(' · ');
}

/* ------------------------------------------------------------------ */
/* Local presentational helpers — mirror the FunnelView KpiCell.      */
/* ------------------------------------------------------------------ */

interface KpiCellProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  focus?: boolean;
}

function KpiCell({ label, value, sub, accent, focus }: KpiCellProps) {
  const cls = ['kpi-cell', accent && 'accent', focus && 'focus']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-meta">{sub}</div>}
    </div>
  );
}
